import { hasAiDetectedLabels } from "@/store/ai-asset-review/ai-asset-review-data-selectors";
import { AiAssetReviewStatus } from "@/store/ai-asset-review/ai-asset-review-slice";
import { IElementMarkup, LabelType } from "@faro-lotv/ielement-types";
import { AiAnnotationStatus } from "../filter-panel/filters/ai-status-filter";
/**
 * Get the AI status of a markup based on its labels
 *
 * @param markup The markup to check
 * @returns The AI status of the markup, or undefined if not AI-generated or not reviewed
 */
export function getMarkupAiStatus(
  markup: IElementMarkup,
): AiAnnotationStatus | undefined {
  if (!hasAiDetectedLabels(markup)) {
    return undefined;
  }

  // Check if it has review status labels (approved/rejected)
  const statusLabel = markup.labels?.find(
    (label) => label.labelType === LabelType.AiReviewStatus,
  );
  if (!statusLabel) {
    return undefined;
  }

  if (statusLabel.name.toLowerCase() === AiAssetReviewStatus.approved) {
    return AiAnnotationStatus.GoodConfidence;
  }

  if (statusLabel.name.toLowerCase() === AiAssetReviewStatus.rejected) {
    return AiAnnotationStatus.BadConfidence;
  }

  // Return undefined if no review status (not reviewed yet)
  return undefined;
}

/**
 * Check if a markup matches the AI status filter
 *
 * @param selectedAiStatuses The selected AI statuses to filter by
 * @param markup The markup to check
 * @returns true if the markup matches the filter or no filter is applied
 */
export function matchAiStatusFilter(
  selectedAiStatuses: AiAnnotationStatus[],
  markup: IElementMarkup,
): boolean {
  // If no AI status filter is selected, show all markups
  if (selectedAiStatuses.length === 0) {
    return true;
  }

  const markupAiStatus = getMarkupAiStatus(markup);

  // If markup is not AI-generated and AI filter is active, hide it
  if (!markupAiStatus) {
    return false;
  }

  // Check if the markup's AI status is in the selected statuses
  return selectedAiStatuses.includes(markupAiStatus);
}
